/**
 * Die elf Bereiche und ihre Symbole (7.2, R149 · 2.15 R217).
 *
 * Der Vorrat ist fest. Ein elfter Bereich ist keine Konfiguration, sondern eine
 * Fachentscheidung — genau wie überall sonst: erweiterbar in der Breite, nicht
 * in der Tiefe (R146).
 *
 * **Und genau so einer ist am 2026-09-09 dazugekommen.** 7.2 hatte festgelegt:
 * „Der Vorrat ist fest … er verschiebt keinen Bereich und legt keinen elften
 * an." Der Konzernanhang legt einen elften an, und 2.15 Abschnitt 6.1
 * begründet das: Er hat eigene Objekte (Angabe, Katalog, Zulieferung), eigene
 * Rechte, einen eigenen Arbeitsvorrat und einen eigenen Ausgabeweg — und er
 * beschäftigt über Wochen Leute, die nie einen Konsolidierungsschritt
 * anfassen: Recht, Personal, Steuern, die Töchter.
 *
 * **Was das die Tastenkürzel kostet:** `Alt+1` bis `Alt+0` sind genau zehn.
 * Der Anhang übernimmt `Alt+0`, *Betrieb* verliert sein Kürzel und rutscht als
 * elfter Eintrag ans Ende. Die Kürzel sind für den Vielnutzer im Februar da
 * (7.2, Abschnitt 4) — der ist im Anhang, nicht in der Systemüberwachung.
 *
 * Alle Symbole sind Strichzeichnungen in einer einzigen Farbe und erben die
 * Schriftfarbe (R151). Sie tragen nie eine Zustandsfarbe — die gehört den
 * gefüllten Zustandssymbolen in `Zustand.tsx`, und die beiden Sprachen dürfen
 * sich nie ähneln. Prüffrage: Kann ein Symbol an beiden Orten stehen, ist es
 * falsch gezeichnet.
 */

import { t } from './sprache'
import type { Sprachschluessel } from './sprache'

/**
 * Die Kennungen der zehn Bereiche.
 *
 * Sie sind zugleich **Adresse** (`#/stammdaten`, R156) und **halber
 * Sprachschlüssel** (`bereich.stammdaten.name`). Beides ist Absicht: Die
 * Kennung ist die Bedeutung und ändert sich nie — der Text daneben ist die
 * Beschriftung und darf in jeder Sprache und in jedem Haus anders lauten
 * (R157). Weil der Typ eine Aufzählung ist und keine Zeichenkette, prüft der
 * Übersetzer mit, dass es zu jedem Bereich auch einen Text gibt.
 */
export type Bereichskennung =
  | 'arbeitsliste'
  | 'abschluss'
  | 'berichte'
  | 'datenimport'
  | 'konzernstruktur'
  | 'stammdaten'
  | 'perioden'
  | 'nachweis'
  | 'anwender'
  | 'anhang'
  | 'betrieb'
  | 'hilfe'

export type Unterpunkt = {
  id: string
  /**
   * Ausgeschrieben statt aus `id` zusammengesetzt: So bricht ein Tippfehler
   * beim Übersetzen ab, statt im Betrieb einen deutschen Satz in der
   * englischen Oberfläche stehen zu lassen.
   */
  schluessel: Sprachschluessel
  /** Steht im Prototyp bereits — alles andere ist als Platzhalter benannt. */
  gebaut?: boolean
  /**
   * Ob dieser Bildschirm die Kontextzeile braucht (R330).
   *
   * Ohne Angabe gilt, was der Bereich sagt. **Gesetzt wird das am Bildschirm
   * und nicht in einer Liste anderswo**: Wer einen neuen Unterpunkt baut,
   * entscheidet hier mit, ob Kreis, Bewertungsebene und Zeitraum für ihn eine
   * Bedeutung haben.
   */
  kontext?: boolean
  /**
   * Ob dieser Bildschirm eine **Tafel** ist (R412).
   *
   * Eine Tafel ist ein Bildschirm, der im Wesentlichen **eine Tabelle** ist,
   * die beliebig lang werden kann. Bei ihr steht die Seite und die Tabelle
   * rollt in sich — nur so bleibt der Tabellenkopf stehen, und nur so sieht
   * man bei Zeile achtzig noch, welche Spalte welche ist.
   *
   * **Ein Blatt mit Formularen ist keine Tafel**, auch wenn eine kleine
   * Tabelle darin steht: Dort soll die Seite rollen, und ein festgehaltener
   * Kopf über drei Zeilen wäre eine Umständlichkeit ohne Ertrag.
   */
  tafel?: boolean
}

export type Bereich = {
  id: Bereichskennung
  /**
   * Der Block, in dem der Bereich steht (7.2 Abschnitt 3a, R324).
   *
   * **Die Gruppe ist eine Beschriftung und keine Ebene.** Sie ordnet die
   * Leiste, sie ist aber kein Ort: Man kann sie nicht anklicken, und ein
   * Bereich wechselt sie nicht. Ohne Gruppe steht der Bereich für sich —
   * die Arbeitsliste oben, die Hilfe am Fuß.
   */
  gruppe?: Sprachschluessel
  /**
   * Steht am Fuß der Leiste statt in der Reihe der Arbeitsbereiche (R326).
   *
   * **Eine Stelle, keine zweite Sorte:** Der Eintrag ist ein Bereich wie jeder
   * andere — gleiches Symbolfeld, gleiche Beschriftung, gleiche Adresse. Unten
   * steht, was *neben* dem Arbeitsfluss liegt und deshalb nicht in seiner
   * Reihe mitgezählt werden soll.
   */
  fuss?: boolean
  /**
   * Ob die Bildschirme dieses Bereichs die Kontextzeile brauchen (R330) —
   * die Vorgabe für seine Unterpunkte.
   */
  kontext?: boolean
  /** Ob die Bildschirme dieses Bereichs Tafeln sind (R412) — die Vorgabe für seine Unterpunkte. */
  tafel?: boolean
  /**
   * Woher der Inhalt fachlich kommt — damit der Platzhalter nicht leer redet.
   *
   * Wird **nicht übersetzt**: Es ist die Fundstelle in einem deutschen
   * Fachkonzept. Eine übersetzte Fundstelle findet niemand wieder.
   */
  herkunft: string
  /**
   * Welches Verb dieser Bereich verlangt (8.1, R155/R358).
   *
   * Ohne Angabe: sichtbar für jeden Angemeldeten. **Die Leiste schützt damit
   * nichts** (R361) — sie lässt weg, was ins Leere führte; geprüft wird an
   * der API.
   */
  verb?: string
  kuerzel: string
  symbol: () => JSX.Element
  unterpunkte: Unterpunkt[]
}

/* --- Die drei Zugriffe auf die Beschriftung. Nirgends sonst steht ein Text. --- */

export function bereichsname(b: Bereich): string {
  return t(`bereich.${b.id}.name`)
}

export function bereichszweck(b: Bereich): string {
  return t(`bereich.${b.id}.zweck`)
}

export function unterpunktname(u: Unterpunkt): string {
  return t(u.schluessel)
}

/* -------------------------------------------------------------------------
   Die zehn Symbole. viewBox 0 0 24 24, Strichstärke 1,5, keine Füllung.
   ------------------------------------------------------------------------- */

function Strich({ children }: { children: React.ReactNode }) {
  return (
    <svg
      className="bereichssymbol"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Liste mit Haken — zwei erledigte Zeilen, eine offene. */
const SymbolArbeitsliste = () => (
  <Strich>
    <path d="M10 6h10M10 12h10M10 18h7" />
    <path d="M3 6l1.7 1.7L7.6 4.4" />
    <path d="M3 12l1.7 1.7L7.6 10.4" />
    <circle cx="5" cy="18" r="2.2" />
  </Strich>
)

/** Drei Stränge, die in einen münden — Konsolidierung als Bild. */
const SymbolAbschluss = () => (
  <Strich>
    <path d="M3 5h3c4 0 5 7 9 7h6" />
    <path d="M3 12h3" />
    <path d="M3 19h3c4 0 5-7 9-7" />
    <path d="M18 9l3 3-3 3" />
  </Strich>
)

/** Blatt mit Balken. */
const SymbolBerichte = () => (
  <Strich>
    <path d="M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
    <path d="M14 3v5h5" />
    <path d="M9 18v-3M12 18v-6M15 18v-4" />
  </Strich>
)

/** Pfeil, der in einen Behälter läuft. */
const SymbolDatenimport = () => (
  <Strich>
    <path d="M12 3v10" />
    <path d="M8 9l4 4 4-4" />
    <path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
  </Strich>
)

/** Organigramm — ein Knoten über zweien. */
const SymbolKonzernstruktur = () => (
  <Strich>
    <rect x="9" y="2.5" width="6" height="4.5" rx="1" />
    <rect x="2" y="16.5" width="6" height="4.5" rx="1" />
    <rect x="16" y="16.5" width="6" height="4.5" rx="1" />
    <path d="M12 7v5.5" />
    <path d="M5 16.5v-4h14v4" />
  </Strich>
)

/** Gestapelte Karteireiter. */
const SymbolStammdaten = () => (
  <Strich>
    <path d="M8 3h8v2.5" />
    <path d="M5.5 6.5h13" />
    <rect x="3" y="9.5" width="18" height="11.5" rx="2" />
    <path d="M3 14h18" />
  </Strich>
)

/**
 * Ein Strahl mit zwei Enden und Monatsstrichen — die Zeitachse als Bild.
 *
 * **Keine Uhr und kein Kalenderblatt.** Eine Uhr zeigt einen Zeitpunkt, ein
 * Kalenderblatt einen Tag; hier geht es um eine Strecke mit Anfang und Ende
 * (R670). Die beiden senkrechten Riegel sind genau das, was gepflegt wird.
 */
const SymbolPerioden = () => (
  <Strich>
    <path d="M3 12h18" />
    <path d="M5 8v8M19 8v8" />
    <path d="M9 10.5v3M12 10.5v3M15 10.5v3" />
  </Strich>
)

/** Kette — hier trägt das Symbol die Sache selbst: die Hash-Verkettung. */
const SymbolNachweis = () => (
  <Strich>
    <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.4-2.4a4 4 0 0 0-5.7-5.7l-1.3 1.3" />
    <path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.4 2.4a4 4 0 0 0 5.7 5.7l1.3-1.3" />
  </Strich>
)

/** Person mit Schlüssel. */
const SymbolAnwender = () => (
  <Strich>
    <circle cx="9" cy="7" r="3.2" />
    <path d="M2.8 20c0-3.4 2.8-5.6 6.2-5.6.9 0 1.8.2 2.6.5" />
    <circle cx="16.8" cy="15.2" r="2.6" />
    <path d="M18.9 16.9l3.1 3.1" />
    <path d="M20.4 18.4l-1.2 1.2" />
  </Strich>
)

/**
 * Ein Fußnotenstern über einer Textzeile.
 *
 * Silhouette geprüft gegen die zehn bestehenden Bereichssymbole (7.2,
 * Abschnitt 5.1): Das nächstverwandte ist *Berichte* (Blatt mit Balken) —
 * Stern und Balken sind bei 20 px und in Graustufen nicht zu verwechseln. Und
 * der Fußnotenstern ist genau das, was eine Anhangsangabe fachlich ist: die
 * Erläuterung zu einer Zahl im Zahlenwerk.
 */
const SymbolAnhang = () => (
  <Strich>
    <path d="M12 3v6.5" />
    <path d="M9.2 4.8l5.6 3.2" />
    <path d="M14.8 4.8l-5.6 3.2" />
    <path d="M4 14h16M4 18h11" />
  </Strich>
)

/** Drei Schieberegler. */
const SymbolHilfe = () => (
  <Strich>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.4 9.4a2.6 2.6 0 1 1 3.4 2.5c-.6.2-.9.7-.9 1.3v.6" />
    <path d="M12 17.2h.01" />
  </Strich>
)

const SymbolBetrieb = () => (
  <Strich>
    <path d="M4 7h6M14.5 7H20" />
    <circle cx="12.2" cy="7" r="2.2" />
    <path d="M4 12h10M18.5 12H20" />
    <circle cx="16.2" cy="12" r="2.2" />
    <path d="M4 17h2.5M11 17h9" />
    <circle cx="8.7" cy="17" r="2.2" />
  </Strich>
)

/* ------------------------------------------------------------------------- */

/** Der Rückfall: Wer nirgends ist, ist auf der Startseite (R140). */
const arbeitslistenbereich: Bereich = {
  id: 'arbeitsliste',
  herkunft: '7.1, R140',
  kuerzel: 'Alt+1',
  symbol: SymbolArbeitsliste,
  unterpunkte: [],
}

export const bereiche: Bereich[] = [
  arbeitslistenbereich,
  {
    id: 'datenimport',
    verb: 'importieren',
    herkunft: '5.1, R132–R139',
    gruppe: 'leiste.gruppe.abschluss',
    kuerzel: 'Alt+2',
    symbol: SymbolDatenimport,
    unterpunkte: [
      { id: 'quellen', schluessel: 'bereich.datenimport.quellen', gebaut: true },
      // Der Fragebogen, aus dem CoKo eine Package-Vorlage erzeugt (5.1
      // Abschnitt 3.37) -- neben den Quellen, weil er eine entwirft.
      { id: 'packagevorlagen', schluessel: 'bereich.datenimport.packagevorlagen', gebaut: true },
      { id: 'lieferungen', schluessel: 'bereich.datenimport.lieferungen', gebaut: true },
      // **Ohne Kopfzeile** (R330, R1852): Die Zuordnungen sind ein
      // Nachschlagewerk — Suche und Gesellschaft reichen, die Spalte *Position*
      // liest jede Gliederung (5.1 Abschnitt 3.68, R1853).
      { id: 'importmapping', schluessel: 'bereich.datenimport.importmapping', gebaut: true, kontext: false },
      // Die Zuordnungsuebersicht ist am 2026-10-06 in die Zuordnungen
      // aufgegangen (5.1 Abschnitt 3.49, R1150).
      { id: 'klaerungsliste', schluessel: 'bereich.datenimport.klaerungsliste' },
      { id: 'ladeprozesse', schluessel: 'bereich.datenimport.ladeprozesse' },
      { id: 'landing', schluessel: 'bereich.datenimport.landing' },
    ],
  },
  {
    id: 'abschluss',
    verb: 'rechnen',
    herkunft: '2.9, 2.10, 2.13',
    gruppe: 'leiste.gruppe.abschluss',
    kuerzel: 'Alt+3',
    symbol: SymbolAbschluss,
    unterpunkte: [
      { id: 'monitor', schluessel: 'bereich.abschluss.monitor', gebaut: true },
      // Das Regelwerk steht vor den Schritten: Ohne eine gueltige Ausgabe
      // rechnet kein Konzernabschluss (2.11 Abschnitt 9, R779).
      { id: 'konzernhandbuch', schluessel: 'bereich.abschluss.konzernhandbuch', gebaut: true },
      // Der Konzernabschluss steht zwischen dem Regelwerk und den Schritten:
      // Er haengt am Kreis, traegt die Prozesse und wird angestossen
      // (2.13 Abschnitt 8, R787).
      { id: 'konzernabschluesse', schluessel: 'bereich.abschluss.konzernabschluesse', gebaut: true },
      { id: 'schritte', schluessel: 'bereich.abschluss.schritte', gebaut: true },
      // **Die IC-Abstimmung steht nicht mehr hier** (Auftrag vom 2026-09-27:
      // „er sollte eigentlich direkt in den KA rein"). Sie ist einer der
      // Prozesse eines Konzernabschlusses, und alles, was zu einem Prozeß
      // gehört, öffnet sein Fenster (R814) — ein eigener Menüpunkt daneben
      // wäre ein zweiter Weg zu derselben Sache.
      // **Kaufpreisallokation und abgeschlossene Abschlüsse stehen nicht
      // mehr hier** (Auftrag vom 2026-09-27): *„Der Status abgeschlossen
      // kommt direkt an einen KA, nicht in einen separaten Bereich. […] Die
      // Kaufpreisallokation ist ebenfalls Bestandteil des KA und kein
      // eigener Menüpunkt."* Beides gehört zum Konzernabschluß — ein
      // Zustand ist keine Seite, und eine Aufteilung des Kaufpreises gehört
      // zu dem Abschluß, für den sie gilt.
    ],
  },
  {
    id: 'anhang',
    herkunft: '2.15, R217–R234',
    gruppe: 'leiste.gruppe.abschluss',
    kuerzel: 'Alt+4',
    symbol: SymbolAnhang,
    unterpunkte: [
      // Das Anhangsbuch steht zuerst: Es ist der Einstieg in den Bereich
      // (2.15, Abschnitt 6.2), so wie das Journal der Einstieg in den
      // Nachweis ist.
      { id: 'anhangsbuch', schluessel: 'bereich.anhang.anhangsbuch', gebaut: true },
      { id: 'zulieferungen', schluessel: 'bereich.anhang.zulieferungen' },
      { id: 'anteilsbesitz', schluessel: 'bereich.anhang.anteilsbesitz' },
      { id: 'angabenkatalog', schluessel: 'bereich.anhang.angabenkatalog', gebaut: true, kontext: false },
      { id: 'vollstaendigkeit', schluessel: 'bereich.anhang.vollstaendigkeit', gebaut: true },
      { id: 'ausgabe', schluessel: 'bereich.anhang.ausgabe' },
    ],
  },
  {
    id: 'berichte',
    // **Jeder Bericht ist eine Tafel** (R412): eine Tabelle, die beliebig lang
    // wird, und ein Kopf, der stehen bleiben muss.
    tafel: true,
    herkunft: '2.12, R102',
    kontext: true,
    gruppe: 'leiste.gruppe.auswerten',
    kuerzel: 'Alt+5',
    symbol: SymbolBerichte,
    unterpunkte: [
      { id: 'bilanz', schluessel: 'bereich.berichte.bilanz', gebaut: true },
      { id: 'guv', schluessel: 'bereich.berichte.guv', gebaut: true },
      // **Die Spezifikation steht im Menü, nicht erst im Bild.** Eine
      // Kapitalflussrechnung ohne die Angabe, nach welchem Schema sie
      // gegliedert ist, lässt sich nicht prüfen: DRS 21 und IAS 7 ordnen
      // Zinsen und Dividenden verschieden zu (2.8, Entscheidung 6). Der
      // Zusatz steht in der Sprachdatei und nicht hier — er ist eine
      // Beschriftung und übersetzbar wie jede andere (R157).
      { id: 'kapitalflussrechnung', schluessel: 'bereich.berichte.kapitalflussrechnung', gebaut: true },
      { id: 'spiegel', schluessel: 'bereich.berichte.spiegel', gebaut: true },
      { id: 'eigenkapitalspiegel', schluessel: 'bereich.berichte.eigenkapitalspiegel', gebaut: true },
      { id: 'kreditspiegel', schluessel: 'bereich.berichte.kreditspiegel', gebaut: true },
      { id: 'abschlussbericht', schluessel: 'bereich.berichte.abschlussbericht' },
      // **Freie Analyse, Annahmenspiegel und Vorjahresvergleich haben keinen
      // Punkt mehr** (Auftrag vom 2026-10-02): geplant, nie gebaut, und ohne
      // erinnerten Zweck nicht zu konzipieren. Beschrieben bleiben sie in 2.12
      // (Abschnitte 2, 3.3 und 3.8), bis darüber neu entschieden ist.
      { id: 'wertsuche', schluessel: 'bereich.berichte.wertsuche' },
    ],
  },
  {
    id: 'nachweis',
    herkunft: '2.13, ADR 008',
    gruppe: 'leiste.gruppe.auswerten',
    kuerzel: 'Alt+6',
    symbol: SymbolNachweis,
    unterpunkte: [
      /*
        Das Journal steht **zuerst**: Es ist das Erste, was ein Prüfer sehen
        will, und das Ziel jedes Links aus einem Wertenachweis (2.14, R209).

        **Ohne Kontextzeile** (R330), seit dem 2026-09-12. Von ihren vier
        Auswahlen steuerten hier drei nichts — Konsolidierungskreis, Raster und
        Arbeitsstand —, und genau davor warnt R330: „Drei Auswahlfelder, die
        nichts steuern, sind nicht bloß überflüssig, sie behaupten etwas."
        Die zwei, die etwas steuerten, sind nicht verschwunden, sondern
        **hereingeholt**: 2.14 Abschnitt 7 gibt dem Journal ohnehin seine
        eigenen Filter — Belegart, Einheit, Periode, Ledger, Konto, Anwender —,
        und die globale Leiste war dafür nur eine Abkürzung.
      */
      { id: 'buchungsjournal', schluessel: 'bereich.nachweis.buchungsjournal', gebaut: true, kontext: false },
      { id: 'aenderungsnachweis', schluessel: 'bereich.nachweis.aenderungsnachweis', gebaut: true },
      { id: 'laeufe', schluessel: 'bereich.nachweis.laeufe' },
      { id: 'prueferansicht', schluessel: 'bereich.nachweis.prueferansicht', gebaut: true },
      { id: 'uebergangene', schluessel: 'bereich.nachweis.uebergangene', gebaut: true },
    ],
  },
  {
    id: 'konzernstruktur',
    herkunft: '2.2, 2.3',
    gruppe: 'leiste.gruppe.einrichten',
    kuerzel: 'Alt+7',
    symbol: SymbolKonzernstruktur,
    unterpunkte: [
      { id: 'struktur', schluessel: 'bereich.konzernstruktur.struktur' },
      { id: 'beteiligungsregister', schluessel: 'bereich.konzernstruktur.beteiligungsregister', gebaut: true },
      { id: 'kreise', schluessel: 'bereich.konzernstruktur.kreise', gebaut: true },
      { id: 'einbeziehung', schluessel: 'bereich.konzernstruktur.einbeziehung' },
      { id: 'ueberleitung', schluessel: 'bereich.konzernstruktur.ueberleitung' },
    ],
  },
  {
    id: 'stammdaten',
    herkunft: '2.1, 2.4–2.6',
    gruppe: 'leiste.gruppe.einrichten',
    kuerzel: 'Alt+8',
    symbol: SymbolStammdaten,
    unterpunkte: [
      { id: 'einheiten', schluessel: 'bereich.stammdaten.einheiten', gebaut: true },
      { id: 'kontenrahmen', schluessel: 'bereich.stammdaten.kontenrahmen', gebaut: true },
      { id: 'positionsplaene', schluessel: 'bereich.stammdaten.positionsplaene', gebaut: true },
      { id: 'ledger', schluessel: 'bereich.stammdaten.ledger', gebaut: true },
      { id: 'bewegungsarten', schluessel: 'bereich.stammdaten.bewegungsarten', gebaut: true },
      // **Die Zeilen der Kapitalflussrechnung haben hier keinen Punkt**
      // (R857, Auftrag vom 2026-10-02): Sie werden in der Rechnung selbst
      // gepflegt — Rechtsklick auf die Zeile, *Eigenschaften*. Der Bereich,
      // der hier einen Tag lang stand, war für den Fachanwender abschreckend.
      { id: 'analysedimensionen', schluessel: 'bereich.stammdaten.analysedimensionen', gebaut: true },
      { id: 'partnercodes', schluessel: 'bereich.stammdaten.partnercodes', gebaut: true },
      // **Der Feldervorrat ist selbst ein Stammdatum** (R554): Wer eine
      // weitere Eigenschaft für alle Gesellschaften braucht, erklärt sie
      // hier — einmal an der Objektart, danach steht sie auf jedem Blatt.
      // **Nicht „Felder"**, sondern nach dem benannt, was der Punkt tut —
      // dieselbe Festlegung wie bei *Export und Import* (R567).
      { id: 'eigenschaften', schluessel: 'bereich.stammdaten.eigenschaften', gebaut: true },
      // **Nicht „Austausch"** (Festlegung des Auftraggebers vom 2026-09-15):
      // Das Wort sagt niemandem, was der Punkt tut. Er heißt nach seinen zwei
      // Handgriffen, und die Schaltflächen darin sagen es noch einmal.
      // **Der Ordnerbaum ueber den Gesellschaften** (R558): eine zweite
      // Gliederung neben der Rechtsstruktur, rein benutzerdefiniert.
      { id: 'ordner', schluessel: 'bereich.stammdaten.ordner', gebaut: true },
      // **Woran ein Bericht haengt** (2.3 Abschnitt 10, R712, 2026-09-24):
      // Bis dahin war das der Konsolidierungskreis, und damit liess sich die
      // Frage „wie sieht die Bilanz dieser einen Gesellschaft aus" gar nicht
      // stellen. Hier wird aufgenommen, was oben im Bericht zur Wahl steht —
      // Kreise, Gesellschaften, Ordner, eigene Dimensionswerte.
      {
        id: 'organisationselemente',
        schluessel: 'bereich.stammdaten.organisationselemente',
        gebaut: true,
      },
      { id: 'exportimport', schluessel: 'bereich.stammdaten.exportimport', gebaut: true },
      // **Der Ländervorrat** (R692, 2026-09-22): Das Sitzland wird
      // ausgewählt und nicht getippt — 249 Länder ab Werk, jedes mit
      // Bezeichnung je Sprache, damit der Anhang „Frankreich" druckt und
      // nicht „FR".
      { id: 'laender', schluessel: 'bereich.stammdaten.laender', gebaut: true },
      // **Die Währungen stehen vor ihren Kursen** (R681, 2026-09-21): Erst
      // der Vorrat, dann die Tabellen, die zwei davon verbinden. Bis dahin
      // war eine Währung ein Textfeld an der Gesellschaft und hatte gar
      // keinen Ort.
      { id: 'waehrungen', schluessel: 'bereich.stammdaten.waehrungen', gebaut: true },
      // **Ohne Kontextzeile** (R330): Eine Kurstabelle hängt an einem
      // Währungspaar und nicht an Kreis, Bewertungsebene und Periode. Der
      // Zeitraum steht über der Tabelle, wo er auch wirkt.
      { id: 'kurse', schluessel: 'bereich.stammdaten.kurse', gebaut: true, kontext: false },
      // **Die historischen Kurse je Gesellschaft** (2.6 Abschnitt 2.12,
      // R1470): neben der Monatstabelle und nicht in ihr — „Die normale
      // FX-Tabelle dient wirklich nur als Bezugsgröße.“ Mit Kontextzeile:
      // Was offen ist, hängt am Monat.
      { id: 'historische-kurse', schluessel: 'bereich.stammdaten.historische-kurse', gebaut: true },
      { id: 'abstimmkreise', schluessel: 'bereich.stammdaten.abstimmkreise' },
    ],
  },
  {
    /*
      Der zwoelfte Bereich (2.19, R670-R675). Der Vorrat ist fest, und ein
      weiterer Bereich ist eine Fachentscheidung -- dieselbe Begründung wie
      beim Konzernanhang (2.15 Abschnitt 6.1): Die Zeitachse hat ein eigenes
      Stammdatum, ein eigenes Recht und eine Wirkung, die jede Lieferung im
      Haus berührt. Sie unter *Stammdaten* zu legen hieße, sie zwischen
      Kontenrahmen und Bewegungsarten zu verstecken -- und sie ist die
      Voraussetzung dafür, dass beide überhaupt bebucht werden können.

      **Alt+9 seit dem 2026-09-26** (R325): Das Kuerzel folgt der Stelle in
      der Leiste, und die neunte ist seit der Aufloesung des Bereichs
      *Regelwerk* (R780) diese hier. Vorher trug sie das Regelwerk, und die
      Zeitachse ging leer aus -- zwoelf Bereiche, zehn Tasten, und leer
      gehen die beiden letzten aus: *Betrieb* und *Hilfe*.
    */
    id: 'perioden',
    verb: 'verwalten',
    herkunft: '2.19, R670–R675',
    gruppe: 'leiste.gruppe.einrichten',
    kuerzel: 'Alt+9',
    symbol: SymbolPerioden,
    unterpunkte: [
      // Ohne Kontextzeile (R330): Die Zeitachse hängt an keinem Kreis, an
      // keiner Bewertungsebene und an keiner Periode -- sie sagt, welche
      // Perioden es überhaupt gibt.
      { id: 'zeitachse', schluessel: 'bereich.perioden.zeitachse', gebaut: true, kontext: false },
    ],
  },
  {
    id: 'anwender',
    verb: 'verwalten',
    herkunft: 'Bereich 8, 2.13 R121',
    gruppe: 'leiste.gruppe.verwaltung',
    kuerzel: 'Alt+0',
    symbol: SymbolAnwender,
    unterpunkte: [
      { id: 'liste', schluessel: 'bereich.anwender.liste', gebaut: true },
      { id: 'rollen', schluessel: 'bereich.anwender.rollen' },
      { id: 'rechte', schluessel: 'bereich.anwender.rechte' },
      { id: 'vieraugen', schluessel: 'bereich.anwender.vieraugen' },
    ],
  },
  {
    id: 'betrieb',
    verb: 'verwalten',
    herkunft: '4.1, 7.1 R147',
    gruppe: 'leiste.gruppe.verwaltung',
    // Ohne Kürzel: Zehn Tasten, zwölf Bereiche (R324). Die letzten beiden
    // Stellen gehen leer aus, und das sind die, die der Vielnutzer im Februar
    // am seltensten braucht. Ein leerer String und kein fehlendes Feld — die
    // Leiste zeigt dann nichts an, statt „undefined" zu schreiben.
    kuerzel: '',
    symbol: SymbolBetrieb,
    unterpunkte: [
      // Seit dem 2026-09-11 gebaut: die zwei Zusagen, die vorher in der
      // Fußzeile standen — Änderungsnachweis und Antwortzeit.
      { id: 'systemzustand', schluessel: 'bereich.betrieb.systemzustand', gebaut: true },
      { id: 'antwortzeiten', schluessel: 'bereich.betrieb.antwortzeiten', gebaut: true },
      { id: 'sprachen', schluessel: 'bereich.betrieb.sprachen', gebaut: true },
      { id: 'sicherung', schluessel: 'bereich.betrieb.sicherung' },
      { id: 'lizenz', schluessel: 'bereich.betrieb.lizenz' },
      { id: 'kundenwuensche', schluessel: 'bereich.betrieb.kundenwuensche' },
    ],
  },
  {
    // Der elfte Bereich (R304). R149 nennt zehn, und das war richtig, solange
    // die Software nur Arbeitsbereiche hatte: Die Hilfe ist keiner. Sie steht
    // nicht im Arbeitsfluss, sondern daneben, und sie gehoert deshalb ans Ende
    // der Leiste und nicht zwischen Berichte und Stammdaten.
    id: 'hilfe',
    herkunft: '7.1, R304',
    kuerzel: '',
    fuss: true,
    symbol: SymbolHilfe,
    unterpunkte: [
      { id: 'import', schluessel: 'bereich.hilfe.import', gebaut: true },
      { id: 'struktur', schluessel: 'bereich.hilfe.struktur', gebaut: true },
      { id: 'gesellschaft', schluessel: 'bereich.hilfe.gesellschaft', gebaut: true },
      // Der Begleiter (7.1 Abschnitt 10, R545): dieselben Anleitungen als
      // Abhakliste, gedacht fuer das zweite Fenster. Er steht am Ende, weil
      // `#/hilfe` weiterhin die erste Anleitung aufschlagen soll.
      { id: 'begleiter', schluessel: 'bereich.hilfe.begleiter', gebaut: true },
    ],
  },
]

/**
 * Ob Kreis, Bewertungsebene und Zeitraum an diesem Ort etwas bewirken (R330).
 *
 * **Die Kontextzeile steht, wo sie wirkt** — dieselbe Entscheidung wie bei
 * dem Zeitwert (R293). Drei Auswahlfelder, die nichts steuern, sind
 * nicht bloß überflüssig: Sie behaupten, der Bildschirm zeige etwas für
 * diesen Kreis und diese Periode. Im Hilfebereich ist das schlicht falsch.
 *
 * **Der Unterpunkt schlägt den Bereich**: Im Datenimport braucht die
 * Lieferung eine Periode, das Verzeichnis der Quellen nicht; unter
 * Stammdaten braucht der Gliederungsbaum eine Bewertungsebene, der Kontenrahmen nicht.
 */
export function brauchtKontext(bereich: string, unterpunkt: string | null): boolean {
  const b = bereiche.find((x) => x.id === bereich)
  if (!b) {
    return false
  }

  const u = unterpunkt ? b.unterpunkte.find((x) => x.id === unterpunkt) : undefined
  return u?.kontext ?? b.kontext ?? false
}

/**
 * Ob dieser Bildschirm eine Tafel ist (R412) — dieselbe Auflösung wie bei der
 * Kontextzeile: Der Unterpunkt entscheidet, sonst der Bereich.
 */
export function istTafel(bereich: string, unterpunkt: string | null): boolean {
  const b = bereiche.find((x) => x.id === bereich)
  if (!b) {
    return false
  }

  const u = unterpunkt ? b.unterpunkte.find((x) => x.id === unterpunkt) : undefined
  return u?.tafel ?? b.tafel ?? false
}

export function findeBereich(id: string): Bereich {
  return bereiche.find((b) => b.id === id) ?? arbeitslistenbereich
}
